import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  ClickAwayListener,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";

export interface InlineDropdownOption<T = string> {
  value: T;
  label: string;
}

interface InlineDropdownEditorProps<T = string> {
  anchorEl: HTMLElement | null;
  open: boolean;

  value: T | null;
  options: InlineDropdownOption<T>[];

  onChange: (value: T) => void;
  onClose: () => void;

  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  placeholder?: string;

  /**
   * Optional callback for custom option filtering.
   * If not provided, filtering is done against the option label.
   */
  filterOptions?: (
    options: InlineDropdownOption<T>[],
    searchText: string
  ) => InlineDropdownOption<T>[];

  /**
   * Allows the editor to be wider than the cell.
   * Defaults to the width of the anchor element.
   */
  minWidth?: number;
  maxWidth?: number;
}

function InlineDropdownEditor<T = string>({
  anchorEl,
  open,
  value,
  options,
  onChange,
  onClose,
  loading = false,
  searchable = true,
  searchPlaceholder = "Search...",
  placeholder = "No options",
  filterOptions,
  minWidth = 250,
  maxWidth = 400,
}: InlineDropdownEditorProps<T>) {
  const [searchText, setSearchText] = React.useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSearchText("");

    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, [open, anchorEl]);

  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) {
      return options;
    }

    if (filterOptions) {
      return filterOptions(options, searchText);
    }

    const search = searchText.trim().toLowerCase();

    return options.filter((option) =>
      option.label.toLowerCase().includes(search)
    );
  }, [options, searchText, filterOptions]);

  const handleSelect = (option: InlineDropdownOption<T>) => {
    onChange(option.value);
    onClose();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
  };

  if (!anchorEl) {
    return null;
  }

  const anchorWidth = anchorEl.getBoundingClientRect().width;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{
        zIndex: 1500,
      }}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 4],
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 8,
          },
        },
      ]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          sx={{
            width: Math.max(anchorWidth, minWidth),
            maxWidth,
            maxHeight: 400,

            display: "flex",
            flexDirection: "column",

            overflow: "hidden",
          }}
        >
          {searchable && (
            <Box
              sx={{
                p: 1,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <TextField
                inputRef={searchInputRef}
                value={searchText}
                onChange={(event) =>
                  setSearchText(event.target.value)
                }
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                size="small"
                fullWidth
                autoComplete="off"
              />
            </Box>
          )}

          <Box
            sx={{
              overflowY: "auto",
              flex: 1,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 3,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : filteredOptions.length > 0 ? (
              <List
                dense
                disablePadding
              >
                {filteredOptions.map((option) => (
                  <ListItemButton
                    key={String(option.value)}
                    selected={option.value === value}
                    onClick={() => handleSelect(option)}
                  >
                    <ListItemText
                      primary={option.label}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  {searchText
                    ? "No results found"
                    : placeholder}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}

export default InlineDropdownEditor;